/* Mock routes — community channels, direct chat and the plant-care assistant. */

import type { ChatMessage, CommunityChannel, Mission } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import {
  ASSISTANT_FALLBACK,
  ASSISTANT_KNOWLEDGE,
  ASSISTANT_LIMITATION,
  ASSISTANT_SUGGESTED_PROMPTS,
} from '../data/content'
import { matchesAny, readString, type MockRoute } from '../types'

export const communityRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/community/channels',
    handler: () =>
      [...db.channels].sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
      ),
    emptyState: [] satisfies CommunityChannel[],
  },
  {
    method: 'GET',
    pattern: '/community/channels/:id/messages',
    handler: ({ params }) => {
      requireChannel(params.id)
      return db.messages
        .filter((message) => message.channelId === params.id)
        .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime())
    },
    emptyState: [] satisfies ChatMessage[],
  },
  {
    method: 'POST',
    pattern: '/community/channels/:id/messages',
    handler: ({ params, body }) => {
      const channel = requireChannel(params.id)
      const text = readString(body, 'body')
      if (!text) {
        throw new ApiError({
          status: 422,
          code: 'empty_message',
          message: 'Write something before sending.',
          fieldErrors: { body: 'The message cannot be empty.' },
        })
      }

      const message: ChatMessage = {
        id: nextId('cm', 100),
        channelId: channel.id,
        author: { id: db.user.id, displayName: db.user.displayName, role: 'user' },
        body: text,
        sentAt: new Date().toISOString(),
        status: 'sent',
      }
      db.messages.push(message)
      channel.lastMessageAt = message.sentAt
      return message
    },
  },
  {
    method: 'GET',
    pattern: '/community/missions',
    handler: () => db.missions satisfies Mission[],
    emptyState: [] satisfies Mission[],
  },
  {
    method: 'POST',
    pattern: '/assistant/messages',
    handler: ({ body }) => {
      const text = readString(body, 'body')
      if (!text) {
        throw new ApiError({
          status: 422,
          code: 'empty_message',
          message: 'Type a question so the assistant has something to answer.',
        })
      }

      const intent = ASSISTANT_KNOWLEDGE.find((entry) => matchesAny(text, entry.keywords))
      const reply = intent?.reply ?? ASSISTANT_FALLBACK

      return {
        id: nextId('as', 900),
        body: reply,
        sentAt: new Date().toISOString(),
        matchedIntentId: intent?.id ?? null,
        suggestedPrompts: ASSISTANT_SUGGESTED_PROMPTS,
        // Stated on every answer: this assistant does not diagnose plants.
        limitations: ASSISTANT_LIMITATION,
      }
    },
  },
]

/** Channel lookup — shared by the message routes. */
function requireChannel(id: string | undefined): CommunityChannel {
  const channel = db.channels.find((item) => item.id === id)
  if (!channel) {
    throw new ApiError({
      status: 404,
      code: 'channel_not_found',
      message: 'That channel is no longer available. It may have been archived.',
    })
  }
  return channel
}
